import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, Layers, Phone, CheckCircle2, ArrowRight, ShieldCheck, Award, Leaf, Clock, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, Overline, SectionHeading } from "@/components/Primitives";
import { IMAGES, WHY_CHOOSE, SERVICES, SERVICE_AREAS } from "@/data/site";

const TRUST = [
  { icon: ShieldCheck, label: "Licensed & Insured" },
  { icon: Award, label: "4.9/5 · 1,200+ Reviews" },
  { icon: Leaf, label: "Eco-Conscious" },
  { icon: Clock, label: "Same-Day Service" },
];

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Technician inspecting a home" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-green/95 via-brand-green/80 to-brand-green/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Overline>Eco-Conscious Solutions · Pest Free Results</Overline>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05] mt-4"
            >
              Protect Your Home & Business from Unwanted Pests
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-lg text-white/80 leading-relaxed max-w-xl"
            >
              Fast, reliable, licensed pest control services for residential and commercial properties.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <Link to="/request-inspection">
                <Button data-testid="hero-inspection-btn" size="lg"
                  className="rounded-full bg-brand-gold text-brand-green font-bold text-base h-14 px-8 hover:bg-brand-goldHover shadow-lg w-full sm:w-auto">
                  Request a Free Inspection <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
              </Link>
              <Link to="/request-inspection">
                <Button data-testid="hero-book-btn" size="lg" variant="outline"
                  className="rounded-full bg-white/10 text-white border-white/30 backdrop-blur-sm font-semibold text-base h-14 px-8 hover:bg-white/20 w-full sm:w-auto">
                  Book an Appointment
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl"
            >
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-2 text-white/90 text-sm font-medium">
                  <t.icon className="h-5 w-5 text-brand-gold shrink-0" /> <span>{t.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline="Why Choose Us" title="The trusted choice for lasting pest protection"
            subtitle="We combine advanced integrated pest management with genuine care for your family, pets, and the planet." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.08}>
                <div className="group bg-white rounded-3xl p-8 border border-black/5 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 h-full">
                  <div className="h-14 w-14 rounded-2xl bg-brand-green/5 flex items-center justify-center group-hover:bg-brand-green transition-colors duration-300">
                    <w.icon className="h-7 w-7 text-brand-green group-hover:text-brand-gold transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-brand-green mt-6">{w.title}</h3>
                  <p className="text-brand-muted text-sm leading-relaxed mt-3">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 lg:py-28 bg-brand-green relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <SectionHeading light overline="Services Overview" title="Complete pest control, one expert team"
              subtitle="From ants to wildlife, we handle every pest problem with proven, eco-conscious methods." />
            <Link to="/services">
              <Button data-testid="services-view-all-btn" variant="outline"
                className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20 font-semibold shrink-0">
                View All Services <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.06}>
                <Link to="/services" className="group block bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl p-6 transition-colors duration-300 h-full">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <s.icon className="h-6 w-6 text-brand-gold" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-white">{s.name}</h3>
                  </div>
                  <p className="text-white/60 text-sm mt-4 leading-relaxed">{s.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EMERGENCY PEST CONTROL */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden bg-brand-charcoal">
            <img src={IMAGES.pest} alt="Pest close up" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center p-10 lg:p-16">
              <div>
                <Overline>Emergency Pest Control</Overline>
                <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white tracking-tight leading-tight mt-3">
                  Pests can't wait. Neither do we.
                </h2>
                <p className="text-white/70 mt-4 leading-relaxed">
                  Wasp swarms, rodent invasions, or a bed bug outbreak? Our emergency response team offers same-day
                  service across Metro Vancouver — 7 days a week.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a href="tel:16723775455">
                    <Button data-testid="emergency-cta-btn" size="lg" className="rounded-full bg-[#E53E3E] hover:bg-[#c53030] text-white font-bold h-14 px-8 w-full sm:w-auto">
                      <Phone className="h-5 w-5 mr-2" /> Call +1 (672) 377-5455
                    </Button>
                  </a>
                  <Link to="/request-inspection">
                    <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-white/10 text-white hover:bg-white/20 h-14 px-8 w-full sm:w-auto">
                      Request Urgent Help
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {["Same-day dispatch", "24/7 phone support", "Licensed responders", "Follow-up guaranteed"].map((f) => (
                  <div key={f} className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
                    <CheckCircle2 className="h-6 w-6 text-brand-gold" />
                    <p className="text-white font-medium mt-3 text-sm">{f}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BEFORE & AFTER */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading center overline="Before & After Results" title="Real transformations, guaranteed"
            subtitle="See the difference professional, eco-conscious treatment makes for homes just like yours." />
          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {[
              { title: "Roach-Free Kitchen", before: "Severe German cockroach activity", after: "Zero sightings in 14 days" },
              { title: "Termite Recovery", before: "Active mud tubes on foundation", after: "Full Sentricon protection installed" },
              { title: "Rodent Exclusion", before: "Attic nesting & gnaw damage", after: "Sealed, sanitized & monitored" },
            ].map((b, i) => (
              <Reveal key={b.title} delay={i * 0.1}>
                <div className="rounded-3xl border border-black/5 overflow-hidden bg-brand-light shadow-sm hover:shadow-lg transition-shadow duration-300">
                  <div className="grid grid-cols-2">
                    <div className="p-6 bg-red-50">
                      <span className="text-xs uppercase tracking-wider font-bold text-red-500">Before</span>
                      <p className="text-sm text-brand-charcoal mt-2 font-medium">{b.before}</p>
                    </div>
                    <div className="p-6 bg-green-50">
                      <span className="text-xs uppercase tracking-wider font-bold text-brand-green">After</span>
                      <p className="text-sm text-brand-charcoal mt-2 font-medium">{b.after}</p>
                    </div>
                  </div>
                  <div className="p-6 border-t border-black/5">
                    <h3 className="font-heading text-lg font-bold text-brand-green">{b.title}</h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionHeading overline="Service Areas" title="Proudly serving Surrey & Metro Vancouver"
              subtitle="Fast, local technicians ready to protect your property across dozens of communities." />
            <div className="grid grid-cols-2 gap-4 mt-8">
              {SERVICE_AREAS.map((a) => (
                <div key={a.region} className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-brand-gold mt-0.5 shrink-0" />
                  <div><p className="font-semibold text-brand-green text-sm">{a.region}</p><p className="text-brand-muted text-xs mt-1">{a.cities.join(", ")}</p></div>
                </div>
              ))}
            </div>
            <Link to="/service-areas"><Button className="rounded-full bg-brand-green text-white hover:bg-brand-greenHover mt-8 font-semibold">Check Your Area <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          </div>
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-black/5 shadow-lg h-[380px]">
              <img src={IMAGES.home} alt="Service area home" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* GUARANTEE BANNER */}
      <section className="pb-4">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[2rem] bg-white border border-brand-gold/40 shadow-sm p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
              <div className="h-16 w-16 rounded-2xl bg-brand-green/5 flex items-center justify-center shrink-0">
                <Layers className="h-8 w-8 text-brand-green" />
              </div>
              <div>
                <h3 className="font-heading text-2xl md:text-3xl font-extrabold text-brand-green">Guaranteed Results</h3>
                <p className="text-brand-muted mt-2 text-base md:text-lg leading-relaxed">
                  If pests return between visits, so do we — free of charge. Guaranteed.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] bg-brand-green p-10 lg:p-16 text-center relative overflow-hidden">
            <div className="relative z-10">
              <Overline>Ready When You Are</Overline>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mt-4 leading-tight">
                Get your free inspection today
              </h2>
              <p className="text-white/70 mt-4 max-w-xl mx-auto">Upload photos, describe the problem, and a licensed technician will reach out within the hour.</p>
              <Link to="/request-inspection">
                <Button data-testid="cta-inspection-btn" size="lg" className="rounded-full bg-brand-gold text-brand-green font-bold h-14 px-10 mt-8 hover:bg-brand-goldHover shadow-lg">
                  Request a Free Inspection <ChevronRight className="h-5 w-5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
